import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { type Campaign } from '@hooks-api/types/dataModel/campaigns'
import { type SetStateAction } from 'react'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { useShallow } from 'zustand/react/shallow'
import { CarrierValidationStatus } from '../core/carrier-validation-status'
import { type FpCampaignCartValues } from '../types'

export const CART_STORAGE_KEY = 'fpCampaignCartStorage'

type FpCampaignCartState = Partial<Record<Campaign['campaign_id'], FpCampaignCartValues>>

export const defaultCartValues: FpCampaignCartValues = {
  cart: {
    items: [],
    addonItems: [],
    specificAddonItems: [],
  },
  coupon: {
    selectedRegularGiveaways: [],
    /**
     * manualInputCouponIds, selectedGiveaways 本次不使用
     */
    manualInputCouponIds: [],
    selectedGiveaways: [],
    redeemedCodes: [],
    hasApplyManuallyRedeemedCodes: false,
    selectedClaimCouponId: null,
    // 點數兌換券僅 WP 購物車消費；FP/STM 不讀此欄位，補型別預設即可，無需 persist version migration。
    selectedExchangeVoucherRedeemCodes: [],
    selectedExchangeVoucherGiveaways: [],
    selectedManualCouponId: null,
  },
  billing: {
    billingCountry: null,
    billingAddressFirstName: '',
    billingAddressPhone: '',
    billingAddressEmail: '',
    billingAddressPostcode: '',
    billingAddressState: '',
    billingAddressCity: '',
    billingAddressAddress1: '',
    billingAddressAddress2: '',
    customerNote: '',
  },
  shipping: {
    appliedShippingMethodId: null,
  },
  payment: {
    appliedPaymentMethodId: null,
    appliedPaymentMethodType: null,
    appliedInstallment: null,
    rememberPaymentCardToken: false,
    bankCode: null,
    userPaymentTokenInfo: null,
    cardSource: '',
  },
  invoice: {
    refundStatement: true,
    receiptType: null,
    businessUbnTitle: '',
    businessUbn: '',
    phoneCarrierNumber: '',
    carrierValidationStatus: CarrierValidationStatus.EMPTY,
    rememberInvoiceCarrier: false,
    charityOrganization: '',
  },
  isGiveawayDispatch: null,
  paymentTransaction: {
    id: null,
    status: null,
    appliedRepaymentMethodId: null,
    orderHash: null,
  },
}

const migrateMap = {
  /**
   * release 2504 原本新增欄位 rewardPoints（SD-4973 會員點數折抵）。
   * SD-20457 後 fastpass 已不再支援點數折抵，FpCampaignCartValues 移除
   * rewardPoints 欄位，故此 migration 不再注入該欄位。
   */
  2504: (persistedState: FpCampaignCartState) => {
    return Object.keys(persistedState).reduce<FpCampaignCartState>((acc, campaignId) => {
      acc[Number(campaignId)] = persistedState[Number(campaignId)] || defaultCartValues
      return acc
    }, {})
  },
  2601: (persistedState: FpCampaignCartState) => {
    return Object.keys(persistedState).reduce<FpCampaignCartState>((acc, campaignId) => {
      acc[Number(campaignId)] = {
        ...(persistedState[Number(campaignId)] || defaultCartValues),
        cart: {
          ...(persistedState[Number(campaignId)]?.cart || defaultCartValues.cart),
          specificAddonItems: [],
        },
      }
      return acc
    }, {})
  },
  /**
   * release 2606 移除 rewardPoints 欄位
   * fastpass 已不再支援點數折抵（SD-20457），跟 sprint 2504 注入欄位的動作對稱地反向收回。
   * 舊用戶 localStorage 內若殘留該欄位，直接刪除，避免結構與當前 type 不一致。
   */
  2606: (persistedState: FpCampaignCartState) => {
    return Object.keys(persistedState).reduce<FpCampaignCartState>((acc, campaignId) => {
      const value = persistedState[Number(campaignId)]
      if (!value) return acc
      const next = { ...value } as FpCampaignCartValues & { rewardPoints?: unknown }
      delete next.rewardPoints
      acc[Number(campaignId)] = next
      return acc
    }, {})
  },
}

const useFpCampaignCartStoreBase = create<FpCampaignCartState>()(
  persist(
    () => ({}),
    {
      name: CART_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      version: 3, // release2606
      migrate: (persistedState, version) => {
        const oldState = persistedState as FpCampaignCartState
        if (version < 1) {
          return migrateMap[2504](oldState)
        }
        if (version < 2) {
          return migrateMap[2601](oldState)
        }
        if (version < 3) {
          return migrateMap[2606](oldState)
        }
        return persistedState
      },
    },
  ),
)

const registerUpdateStore = () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return
  // 當使用者切換頁面時，重新hydrate store
  const updateStore = () => {
    useFpCampaignCartStoreBase.persist.rehydrate()
  }

  document.addEventListener('visibilitychange', updateStore)
  window.addEventListener('focus', updateStore)
}
registerUpdateStore()

export const useFpCampaignCartStore = (campaignId: number | null) => {
  if (!campaignId) {
    throw new Error('CampaignId is required')
  }
  const cartValues = useFpCampaignCartStoreBase(state => state[campaignId] || defaultCartValues)

  const setState = useCallbackEvent((value: SetStateAction<FpCampaignCartValues>) => {
    if (!campaignId) {
      throw new Error('CampaignId is required')
    }
    useFpCampaignCartStoreBase.setState((prevState) => {
      const nextState = typeof value === 'function' ? value(prevState[campaignId] || defaultCartValues) : value
      return {
        ...prevState,
        [campaignId]: nextState,
      }
    })
  })

  return [cartValues, setState] as const
}

export const useFpCampaignCartSelector = <U>(campaignId: number | null, selector: (state: FpCampaignCartValues) => U) => {
  if (!campaignId) {
    throw new Error('CampaignId is required')
  }
  return useFpCampaignCartStoreBase(useShallow(state => selector(state[campaignId] || defaultCartValues)))
}

/**
 * 取得所有 FastPass Campaign 購物車商品（一般商品 + addon_v2 商品）
 */
export const useFpCampaignAllItems = (campaignId: number | null) => {
  return useFpCampaignCartSelector(campaignId, state => [
    ...state.cart.items,
    ...(state.cart.addonItems || []),
  ])
}
